export type OperationStep = {
  key: string;
  label: string;
  status: "pending" | "active" | "complete" | "blocked" | "failed";
  walletFamily?: string | null;
  chain?: string | null;
  txHash?: string | null;
  updatedAt?: string | null;
};

export type PublicOperation = {
  id: string;
  sourceChain: string;
  destinationChain: string;
  sourceDomain: number;
  destinationDomain: number;
  amount: string;
  finality: "standard" | "fast";
  status: string;
  walletAddress?: string | null;
  recipientAddress?: string | null;
  sourceTxHash?: string | null;
  destinationTxHash?: string | null;
  gasTopupUsd?: string | null;
  gasStatus?: string | null;
  gasPaymentTxHash?: string | null;
  gasDeliveryTxHash?: string | null;
  gasNativeAmount?: string | null;
  gasNativeSymbol?: string | null;
  gasReferencePriceUsd?: string | null;
  gasMarkupBps?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  failedStep?: string | null;
  requiredWalletFamily?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  steps: OperationStep[];
};

export type RecentRecipient = {
  address: string;
  destinationChain: string;
  destinationDomain: number;
  lastUsedAt: string;
};

export type AddressBookEntry = {
  address: string;
  label: string;
  createdAt: string;
};

const HISTORY_KEY = "scc:operationHistory";
const ADDRESS_BOOK_KEY = "scc:addressBook";

export function operationUrl(id: string) {
  return `${window.location.origin}/op/${encodeURIComponent(id)}`;
}

export function saveOperationToHistory(id: string) {
  try {
    const current = readOperationHistory();
    const next = [id, ...current.filter((value) => value !== id)].slice(0, 50);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    // Local history is a convenience layer only. PostgreSQL remains the operation source of truth.
  }
}

export function readOperationHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === "string" && /^scc_[0-9a-f-]{36}$/i.test(value)).slice(0, 50);
  } catch {
    return [];
  }
}

export function removeOperationFromHistory(id: string) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(readOperationHistory().filter((value) => value !== id)));
  } catch {
    // noop
  }
}

export function readAddressBook(): AddressBookEntry[] {
  try {
    const raw = localStorage.getItem(ADDRESS_BOOK_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((value): value is AddressBookEntry => Boolean(value && typeof value.address === "string" && typeof value.label === "string" && typeof value.createdAt === "string"))
      .slice(0, 100);
  } catch {
    return [];
  }
}

export function saveAddressBookEntry(address: string, label: string) {
  const normalized = address.toLowerCase();
  const cleanLabel = label.trim().slice(0, 80) || "Saved address";
  const current = readAddressBook();
  const next: AddressBookEntry[] = [
    { address, label: cleanLabel, createdAt: new Date().toISOString() },
    ...current.filter((entry) => entry.address.toLowerCase() !== normalized)
  ].slice(0, 100);
  localStorage.setItem(ADDRESS_BOOK_KEY, JSON.stringify(next));
  return next;
}

export function removeAddressBookEntry(address: string) {
  const normalized = address.toLowerCase();
  const next = readAddressBook().filter((entry) => entry.address.toLowerCase() !== normalized);
  localStorage.setItem(ADDRESS_BOOK_KEY, JSON.stringify(next));
  return next;
}

export async function readApiJson<T>(response: Response, fallback: string): Promise<T> {
  const text = await response.text();
  if (!text.trim()) throw new Error(response.ok ? fallback : `${fallback} (HTTP ${response.status})`);

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(response.ok ? fallback : `${fallback} (HTTP ${response.status})`);
  }

  if (!response.ok) {
    const apiError = data && typeof data === "object" && "error" in data ? (data as { error?: unknown }).error : undefined;
    throw new Error(typeof apiError === "string" && apiError ? apiError : `${fallback} (HTTP ${response.status})`);
  }
  return data as T;
}

export async function fetchOperation(id: string): Promise<PublicOperation> {
  const response = await fetch(`/api/operations/${encodeURIComponent(id)}`, { headers: { accept: "application/json" } });
  return readApiJson<PublicOperation>(response, "Operation is temporarily unavailable.");
}

export async function fetchRecentRecipients(): Promise<RecentRecipient[]> {
  const ids = readOperationHistory().slice(0, 30);
  if (ids.length === 0) return [];
  const results = await Promise.allSettled(ids.map((id) => fetchOperation(id)));
  const unique = new Map<string, RecentRecipient>();
  for (const result of results) {
    if (result.status !== "fulfilled") continue;
    const operation = result.value;
    if (operation.status !== "COMPLETED" || !operation.recipientAddress) continue;
    if (operation.walletAddress && operation.walletAddress.toLowerCase() === operation.recipientAddress.toLowerCase()) continue;
    const key = operation.recipientAddress.toLowerCase();
    const candidate: RecentRecipient = {
      address: operation.recipientAddress,
      destinationChain: operation.destinationChain,
      destinationDomain: operation.destinationDomain,
      lastUsedAt: operation.completedAt ?? operation.updatedAt
    };
    const current = unique.get(key);
    if (!current || Date.parse(candidate.lastUsedAt) > Date.parse(current.lastUsedAt)) unique.set(key, candidate);
  }
  return [...unique.values()].sort((a, b) => Date.parse(b.lastUsedAt) - Date.parse(a.lastUsedAt)).slice(0, 8);
}

export async function patchOperation(id: string | null, body: Record<string, unknown>) {
  if (!id) return;
  try {
    await fetch(`/api/operations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true
    });
  } catch {
    // Operation persistence must not block wallet execution.
  }
}
